import { PRIVACY_KIND_TITLES, PRIVACY_KINDS, type PrivacyRequest, type Subprocessor } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['admin', 'privacy-requests'];

const STATUS: Record<PrivacyRequest['status'], { title: string; tone: Tone }> = {
  new: { title: 'Новый', tone: 'info' },
  in_progress: { title: 'В работе', tone: 'warning' },
  done: { title: 'Выполнен', tone: 'success' },
  rejected: { title: 'Отклонён', tone: 'neutral' },
};

function NewRequest({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<PrivacyRequest['kind']>('export');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [received, setReceived] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<PrivacyRequest>('/admin/privacy-requests', {
        method: 'POST',
        body: { kind, subjectName: name, subjectEmail: email, receivedOn: received || undefined },
      }),
    onSuccess: () => {
      toast('Запрос записан');
      qc.invalidateQueries({ queryKey: KEY });
      setName('');
      setEmail('');
      setReceived('');
      onClose();
    },
  });
  const errs = (create.error as { fieldErrors?: () => Record<string, string> } | null)?.fieldErrors?.() ?? {};
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Новый запрос субъекта данных"
      description="Срок ответа человеку — 30 дней со дня получения."
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Что просит человек" htmlFor="pr-kind">
          <Select
            id="pr-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as PrivacyRequest['kind'])}
          >
            {PRIVACY_KINDS.map((k) => (
              <option key={k} value={k}>
                {PRIVACY_KIND_TITLES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Имя человека" htmlFor="pr-name" error={errs.subjectName}>
          <Input id="pr-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Почта человека" htmlFor="pr-email" error={errs.subjectEmail}>
          <Input id="pr-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Дата получения" htmlFor="pr-date" hint="Если пусто — сегодня">
          <Input id="pr-date" type="date" value={received} onChange={(e) => setReceived(e.target.value)} />
        </Field>
        {create.isError ? <ErrorBox error={create.error} /> : null}
      </div>
    </Dialog>
  );
}

function Handle({ req, onClose }: { req: PrivacyRequest | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<PrivacyRequest['status']>('in_progress');
  const [result, setResult] = useState('');
  const [seen, setSeen] = useState<string | null>(null);
  if (req && seen !== req.id) {
    setSeen(req.id);
    setStatus(req.status === 'new' ? 'in_progress' : req.status);
    setResult(req.result ?? '');
  }
  const save = useMutation({
    mutationFn: () =>
      api<PrivacyRequest>(`/admin/privacy-requests/${req?.id}`, {
        method: 'PATCH',
        body: { status, result: result.trim() || null },
      }),
    onSuccess: () => {
      toast('Запрос обновлён');
      qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(req)}
      onOpenChange={(o) => !o && onClose()}
      title="Обработка запроса"
      description={req ? `${PRIVACY_KIND_TITLES[req.kind]} · ${req.subjectName}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Состояние" htmlFor="ph-status">
          <Select
            id="ph-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as PrivacyRequest['status'])}
          >
            {(Object.keys(STATUS) as PrivacyRequest['status'][]).map((s) => (
              <option key={s} value={s}>
                {STATUS[s].title}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Что сделано"
          htmlFor="ph-result"
          hint={
            status === 'done' || status === 'rejected'
              ? 'Обязательно: что сделано или почему отказано'
              : 'Можно заполнить позже'
          }
        >
          <Textarea
            id="ph-result"
            value={result}
            maxLength={2000}
            onChange={(e) => setResult(e.target.value)}
          />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

/** Администрирование → Персональные данные (МР-10): запросы людей и список субпроцессоров */
export function PrivacyPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<PrivacyRequest[]>('/admin/privacy-requests') });
  const sp = useQuery({
    queryKey: ['company', 'subprocessors'],
    queryFn: () => api<Subprocessor[]>('/company/subprocessors'),
  });
  const [adding, setAdding] = useState(false);
  const [handling, setHandling] = useState<PrivacyRequest | null>(null);
  return (
    <div className="flex flex-col gap-5">
      <Panel
        title="Запросы субъектов данных"
        meta="МР-10"
        actions={
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
            Записать запрос
          </Button>
        }
      >
        <p className="mb-3 text-[13px] text-ink-2">
          Человек вправе попросить выгрузить, исправить или удалить свои данные. Ответить нужно за 30 дней.
        </p>
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorBox error={q.error} /> : null}
        {q.data && !q.data.length ? <p className="text-[14px] text-ink-2">Запросов пока нет.</p> : null}
        {q.data?.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.id} className="flex flex-col gap-1.5 py-3 text-[14px]">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <span className="font-medium text-ink [overflow-wrap:anywhere]">
                    {PRIVACY_KIND_TITLES[r.kind]} · {r.subjectName}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {r.overdue ? <Badge tone="danger">Просрочен</Badge> : null}
                    <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].title}</Badge>
                  </span>
                </div>
                <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {r.subjectEmail} · получен {date(r.receivedOn)}, ответить до {date(r.dueOn)}
                </div>
                {r.result ? <div className="text-[13px] text-ink-2">Итог: {r.result}</div> : null}
                <div>
                  <Button size="sm" onClick={() => setHandling(r)}>
                    Обработать
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </Panel>

      <Panel title="Субпроцессоры" meta="кто ещё обрабатывает данные по поручению портала">
        {sp.isPending ? <Spinner /> : null}
        {sp.isError ? <ErrorBox error={sp.error} /> : null}
        {sp.data && !sp.data.length ? (
          <p className="text-[14px] text-ink-2">Список пока не заполнен.</p>
        ) : null}
        {sp.data?.length ? (
          <ul className="divide-y divide-line">
            {sp.data.map((s) => (
              <li key={s.name} className="flex flex-col gap-0.5 py-2.5 text-[14px]">
                <span className="font-medium text-ink">{s.name}</span>
                <span className="text-[13px] text-ink-2">
                  {s.purpose}
                  {s.country ? ` · ${s.country}` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Panel>
      <NewRequest open={adding} onClose={() => setAdding(false)} />
      <Handle req={handling} onClose={() => setHandling(null)} />
    </div>
  );
}
